<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('General Settings') }}
        <v-btn
          v-if="subdomain"
          class="ml-2"
          small
          text
          color="primary"
          :href="`https://${subdomain}.bookingsboom.com`"
          target="_blank"
          rel="noopener"
        >
          <v-icon small class="mr-1">mdi-open-in-new</v-icon>
          {{ `${subdomain}.bookingsboom.com` }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 mb-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Site Settings') }}
                </v-card-title>

                <v-text-field
                  v-model.trim="formData.name"
                  dense
                  outlined
                  :rules="[required, nameRule]"
                  hint="* Only lowercase letters, numbers, and hyphens allowed"
                  :persistent-hint="true"
                  :label="`${$t('Name')} (subdomain)`"
                  @input="updateForm"
                />

                <v-text-field
                  v-model.trim="formData.site_title"
                  dense
                  outlined
                  :rules="[required]"
                  :label="$t('Website Title')"
                  @input="updateForm"
                />

                <v-text-field
                  v-model.trim="formData.custom_domain"
                  dense
                  outlined
                  hide-details
                  :label="$t('Custom Domain (Optional)')"
                  @input="updateForm"
                />

                <div class="pl-1 text-body-2">
                  {{
                    `Your domain: ${
                      formData.custom_domain || `${subdomain}.bookingsboom.com`
                    }`
                  }}
                </div>
                <div class="pl-1 text-caption grey--text">
                  * Domain modifications may take several hours to take effect.
                </div>

                <v-select
                  v-model="formData.site_type"
                  dense
                  outlined
                  :items="siteTypes"
                  :label="$t('Site Type')"
                  @change="updateForm"
                />
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined class="pa-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Analytics') }}
                </v-card-title>

                <v-text-field
                  v-model="formData.analytics.google"
                  dense
                  outlined
                  :label="$t('Google Analytics (gtag)')"
                  :hint="$t('Add your Google tag ID from Google Analytics')"
                  persistent-hint
                  @input="updateForm"
                />

                <v-text-field
                  v-model="formData.analytics.gtm"
                  dense
                  outlined
                  :label="$t('Google Tag Manager (gtm)')"
                  @input="updateForm"
                />

                <v-text-field
                  v-model="formData.analytics.pixel"
                  dense
                  outlined
                  :label="$t('Facebook Pixel (Pixel Id)')"
                  @input="updateForm"
                />

                <v-checkbox
                  v-model="formData.show_cookie_consent"
                  :label="$t('Show Cookie Consent Banner')"
                  :hint="$t('Display a cookie consent banner to visitors')"
                  persistent-hint
                  @change="updateForm"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FormRulesMixin from 'components/mixins/form-rules-mixin'

export default {
  name: 'GeneralSettings',

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formData: {
        name: this.bookingEngineSetting.name || '',
        site_title: this.bookingEngineSetting.site_title || '',
        custom_domain: this.bookingEngineSetting.custom_domain || '',
        site_type: this.bookingEngineSetting.site_type || 'vacation_rental',
        analytics: {
          google: this.bookingEngineSetting.analytics?.google || '',
          gtm: this.bookingEngineSetting.analytics?.gtm || '',
          pixel: this.bookingEngineSetting.analytics?.pixel || '',
        },
        show_cookie_consent:
          this.bookingEngineSetting.show_cookie_consent || false,
      },
      siteTypes: [
        { text: this.$t('Vacation Rental'), value: 'vacation_rental' },
        { text: this.$t('Hotel'), value: 'hotel' },
        { text: this.$t('Agent'), value: 'agent' },
      ],
    }
  },

  computed: {
    subdomain() {
      return this.formData.name
        ? this.formData.name
            .replace(/ /g, '')
            .replace(/\./g, '')
            .replace(/_/g, '')
            .toLowerCase()
        : ''
    },
  },

  watch: {
    bookingEngineSetting: {
      handler(newVal) {
        this.formData = {
          name: newVal.name || '',
          site_title: newVal.site_title || '',
          custom_domain: newVal.custom_domain || '',
          site_type: newVal.site_type || 'vacation_rental',
          analytics: {
            google: newVal.analytics?.google || '',
            gtm: newVal.analytics?.gtm || '',
            pixel: newVal.analytics?.pixel || '',
          },
          show_cookie_consent: newVal.show_cookie_consent || false,
        }
      },
      deep: true,
    },
  },

  methods: {
    nameRule(v) {
      if (!v) return true
      const pattern = /^[a-z0-9-]+$/
      return (
        pattern.test(v) ||
        'Only lowercase letters, numbers, and hyphens allowed'
      )
    },

    updateForm() {
      if (this.$refs.form.validate()) {
        const updatedData = {
          ...this.bookingEngineSetting,
          ...this.formData,
        }
        this.$emit('update:setting', updatedData)
      }
    },

    validate() {
      return this.$refs.form.validate()
    },
  },
}
</script>
